import React, { useCallback, useState, useEffect } from 'react';
import Uploader from 'components/uploader';
import { propEq, reject } from 'ramda';
import { Download, Trash2 } from 'react-feather';
import { JpegIcon } from 'components/svgs/JpegIcon';
import { PdfIcon } from 'components/svgs/PdfIcon';
import { PngIcon } from 'components/svgs/PngIcon';
import {
  batchUploadAttachments,
  getAttachmentsForPolicyWithLinks,
  deleteAttachment,
  batchDeleteAttachments,
} from 'services';
import { Button } from 'components/quashed';
import DocViewer from 'components/doc-viewer';
import Spinner from 'components/spinner';
import styles from '../styles/PolicyDetailsDocs.module.scss';
import { IPopupProps, useUploader } from 'hooks/useUploader';
import {
  UPLOAD_FAILED_MESSAGE,
  UPLOAD_MAX_SIZE_EXCEEDED,
} from 'constants/errors';
import { IEmployer, IPolicy, IPolicyRenewal, IPolicyScan, IUser } from 'types';
import { trackUploads } from 'actions';
import { useDispatch } from 'react-redux';
import ConfirmAction from 'components/confirm-action';
import {
  getUtcDateNow,
  trackPolicyDetailsDocClick,
  trackPolicyDetailsDocDelete,
  sortByCreatedAtDesc,
} from 'helpers';
import history from 'helpers/history';
import { HOME_ROUTE } from 'constants/routes';
import { getOtherDocs, isMbiPurchased, isRenewalAttachment } from '../utils';

export interface IDocsProps {
  disabled: boolean;
  employer: IEmployer;
  hideTitle?: boolean;
  isOwner: boolean;
  pendingRenewal?: IPolicyRenewal;
  policy: IPolicy;
  policyScanId: string;
  showUploadBtn?: boolean;
  user: IUser;
  updatePolicy?(p: IPolicy): void;
  updatePolicyScan?(ps: IPolicyScan): void;
}

const handleMSPurchaseUpload = (
  policy,
  policyScanId,
  skipTextract,
  updatePolicy,
  updatePolicyScan,
) => {
  const toUpdate = {
    ...policy,
    processed: skipTextract,
    isPurchaseUpload: true,
  };

  history.replace(HOME_ROUTE);
  updatePolicyScan({
    id: policyScanId,
    sk: policy.id,
    purchasedAt: getUtcDateNow(),
  });
  updatePolicy(toUpdate, false);
};

const MAX_FILENAME_LENGTH = 35;

const truncateFileName = (fileName: string): string => {
  if (!fileName || fileName.length <= MAX_FILENAME_LENGTH) return fileName;

  const extensionStartIndex = fileName.lastIndexOf('.');
  if (extensionStartIndex <= 0) return fileName.slice(0, MAX_FILENAME_LENGTH - 3) + '...';

  const extension = fileName.slice(extensionStartIndex + 1);
  const availableLength = MAX_FILENAME_LENGTH - extension.length - 5;

  return availableLength > 0
    ? fileName.slice(0, availableLength) + '....' + extension
    : fileName.slice(0, MAX_FILENAME_LENGTH - 3) + '...';
};

const FILE_ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  jpeg: JpegIcon,
  jpg: JpegIcon,
  pdf: PdfIcon,
  png: PngIcon,
};

const getFileIcon = (fileName: string) => {
  if (!fileName) return null;
  const parts = fileName.split('.');
  const extension = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  return FILE_ICON_MAP[extension] || null;
};

export const AttachmentItem = ({
  attachment,
  onClick,
  onDelete,
  onDownload,
}) => {
  const { fileName } = attachment;
  const displayName = truncateFileName(fileName);
  const Icon = getFileIcon(fileName);

  const handleClick = () => onClick(attachment);

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownload(attachment);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(attachment);
  };

  return (
    <div className={styles.docRow}>
      <div className={styles.docInfo} onClick={handleClick}>
        {Icon && <Icon className={styles.docIcon} />}
        <span className={styles.docFileName} title={fileName}>
          {displayName}
        </span>
      </div>
      <div className={styles.docActions}>
        {onDownload && (
          <button className={styles.actionBtn} onClick={handleDownload}>
            <span className={styles.actionText}>Download</span>
            <Download className={styles.actionIcon} size={16} />
          </button>
        )}
        {onDelete && (
          <button className={styles.actionBtn} onClick={handleDelete}>
            <span className={styles.actionText}>Delete</span>
            <Trash2 className={styles.actionIcon} size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

const PolicyDetailsDocs = ({
  disabled,
  employer,
  hideTitle,
  isOwner,
  pendingRenewal,
  policy,
  policyScanId,
  showUploadBtn,
  user,
  updatePolicy,
  updatePolicyScan,
}: IDocsProps) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [pdfSrc, setPdfSrc] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [error, setError] = useState(null);
  const [popupProps, setPopupProps] = useState<IPopupProps>({ open: false });
  const [showConfirmOverwriteModal, setShowConfirmOverwriteModal] = useState(
    false,
  );
  const [filesToUpload, setFilesToUpload] = useState(null);
  const [rejectedOnUpload, setRejectedOnUpload] = useState(null);
  const { id: policyId } = policy;

  const {
    attachments,
    uploaderRef,
    setAttachments,
    openUploader,
    findNewAndSimilarFiles,
  } = useUploader();

  const otherDocs = getOtherDocs(policy, employer?.policies, user);

  const isEmpty = !(attachments.length + otherDocs.length);

  const handleConfirmCancel = useCallback(() => setDeleteDoc(null), []);

  const updateDocuments = useCallback(async () => {
    setLoading(true);
    const docs = await getAttachmentsForPolicyWithLinks(policyId);
    setAttachments(docs);
    setLoading(false);
  }, [policyId]);

  useEffect(() => {
    if (policyId) {
      updateDocuments();
    }
  }, [policyId]);

  const handleDeleteDocument = useCallback(
    async (docId, key) => {
      await deleteAttachment(docId, policyId, key);
      const docs = reject(propEq('id', docId), attachments);
      setAttachments(docs);
    },
    [policyId, attachments],
  );

  const handleUploadOnDrop = useCallback(
    async (files, rejected) => {
      if (policyScanId && !policy.uploadedAt && !policy.policyProviderName) {
        handleUploadDocument(files, rejected, false);
      } else {
        setShowConfirmOverwriteModal(true);
        setFilesToUpload(files);
        setRejectedOnUpload(rejected);
      }
    },
    [showConfirmOverwriteModal, filesToUpload, rejectedOnUpload],
  );

  const handleUploadDocument = useCallback(
    async (files, rejected, skipTextract) => {
      if (isOwner) {
        try {
          if (files.length) {
            const [similarFiles, newFiles] = findNewAndSimilarFiles(
              files,
              attachments,
            );

            if (similarFiles.length > 0) {
              setPopupProps({
                open: true,
                files: similarFiles,
                newFiles,
              });
              setAttachments(R.concat(attachments, newFiles));
            } else {
              setLoading(true);
              await batchUploadAttachments(files, policyId, skipTextract);
              trackUploads(user, dispatch);

              if (policyScanId && !policy.uploadedAt) {
                await handleMSPurchaseUpload(
                  policy,
                  policyScanId,
                  skipTextract,
                  updatePolicy,
                  updatePolicyScan,
                );
              }
              updateDocuments();
            }
          } else if (rejected.length) {
            setError(UPLOAD_MAX_SIZE_EXCEEDED);
          }
        } catch {
          setError(UPLOAD_FAILED_MESSAGE);
        }
        setLoading(false);
      }
    },
    [isOwner, policyId, updateDocuments, attachments, policyScanId],
  );
  const handleConfirmOverwrite = () => {
    setShowConfirmOverwriteModal(false);
    handleUploadDocument(filesToUpload, rejectedOnUpload, false);
  };

  const handleDeclineOverwrite = () => {
    setShowConfirmOverwriteModal(false);
    handleUploadDocument(filesToUpload, rejectedOnUpload, true);
  };

  const handlePdfViewerClose = useCallback(() => setPdfSrc(null), []);

  const handleConfirm = useCallback(() => {
    handleDeleteDocument(deleteDoc.id, deleteDoc.key);
    trackPolicyDetailsDocDelete(deleteDoc);
    setDeleteDoc(null);
  }, [handleDeleteDocument, deleteDoc]);

  const handlePopupAction = async (confirm: boolean = false) => {
    let simFiles = [];
    let updated = [];
    setLoading(true);
    if (confirm && popupProps.files) {
      [simFiles] = findNewAndSimilarFiles(attachments, popupProps.files);
      updated = R.concat(popupProps.files, popupProps?.newFiles);
      await batchDeleteAttachments(simFiles);
      setPopupProps({ open: false });
    } else {
      updated = popupProps?.newFiles;
      setPopupProps({ open: false });
    }
    try {
      await batchUploadAttachments(updated, policyId);
      trackUploads(user, dispatch);
      setLoading(false);
    } catch {
      setLoading(false);
      setError(UPLOAD_FAILED_MESSAGE);
    }
  };

  const handleViewClick = (src) => {
    setPdfSrc(src.href);
    trackPolicyDetailsDocClick({ src });
  };

  const handleDownload = (att) => {
    const a = document.createElement('a');
    a.href = att.href;
    a.download = att.fileName;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (isEmpty && !isOwner) {
    return null;
  }

  const sortedAttachments = sortByCreatedAtDesc(attachments);

  return (
    <>
      <DocViewer src={pdfSrc} onClose={handlePdfViewerClose} />
      <ConfirmAction
        open={showConfirmOverwriteModal}
        className={styles.uploadModal}
        header="You've uploaded a new file"
        content={
          'Would you like to update your policy information with your new file? Please note this will overwrite your existing information.'
        }
        onCancel={handleDeclineOverwrite}
        onConfirm={handleConfirmOverwrite}
        confirmText="Yes, please overwrite"
        cancelText="No, do not overwrite"
      />
      <ConfirmAction
        open={!!deleteDoc}
        header="Delete Document"
        content={`Are you sure you want to delete ${
          deleteDoc ? deleteDoc.fileName : 'document'
        }?`}
        onCancel={handleConfirmCancel}
        onConfirm={handleConfirm}
      />
      <div className={styles.container}>
        {!hideTitle && (
          <div className={styles.header}>
            <h2>My documents</h2>
            {showUploadBtn && (
              <Button
                id="PolicyDetails_BrowseBtn"
                className={styles.uploadBtn}
                inverted={true}
                onClick={openUploader}
              >
                <span>
                  Upload <span>documents</span>
                </span>
              </Button>
            )}
          </div>
        )}

        <div className={styles.content}>
          {loading ? (
            <div className={styles.placeholder}>
              <Spinner loading={true} />
            </div>
          ) : (
            <Uploader
              ref={uploaderRef}
              onDrop={handleUploadOnDrop}
              className={styles.uploader}
              isEmpty={isEmpty}
              isUploading={loading}
              popupProps={popupProps}
              onPopupAction={handlePopupAction}
            />
          )}

          {isEmpty ? (
            !loading && (
              <div className={styles.placeholder}>
                {isMbiPurchased(policy)
                  ? 'Your document is being processed. If it is not available within a minute, please refresh the page.'
                  : 'No documents added'}
              </div>
            )
          ) : (
            <div className={styles.documentsContainer}>
              <div className={styles.documents}>
                {sortedAttachments.map((att) => {
                  const allowDelete =
                    isOwner &&
                    !disabled &&
                    !isRenewalAttachment(pendingRenewal, att.fileName);

                  return (
                    <AttachmentItem
                      key={att.id}
                      attachment={att}
                      onClick={handleViewClick}
                      onDelete={allowDelete && setDeleteDoc}
                      onDownload={handleDownload}
                    />
                  );
                })}
                {/* Same as rendering attachments but without the delete button */}
                {/* Did not combine it with attachments so we dont have to */}
                {/* put a flag on the otherDocs */}
                {otherDocs.map((att, idx) => (
                  <AttachmentItem
                    key={`otherDoc_${idx}`}
                    attachment={att}
                    onClick={handleViewClick}
                    onDelete={null}
                    onDownload={handleDownload}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PolicyDetailsDocs;
